import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import PostCommunityCard from "../components/cards/PostCommunityCard";
import {
  useGetPostQuery,
  useCreateCommentMutation,
  useLikePostMutation,
  useUnlikePostMutation,
} from "../services/users/community";
import {
  useAdminGetOrganizationPostQuery,
  useAdminLikeEmployeePostMutation,
  useAdminUnlikeEmployeePostMutation,
  useAdminCommentOnEmployeePostMutation,
  useAdminLikeEmployeeCommentMutation,
  useAdminUnlikeEmployeeCommentMutation,
} from "../services/organization/adminUsers";
import NotFoundPage from "./404";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const SinglePost = () => {
  let { id } = useParams();
  const { t } = useTranslation("common");

  const isAdmin = useMemo(
    () => localStorage.getItem("is_admin") === "true",
    []
  );

  const {
    data: employeePostResp,
    isLoading: isEmpLoading,
    refetch: refetchEmployeePost,
  } = useGetPostQuery(id, { skip: isAdmin });

  const {
    data: orgPostResp,
    isLoading: isOrgLoading,
    refetch: refetchOrgPost,
  } = useAdminGetOrganizationPostQuery(id, {
    skip: !isAdmin,
  });

  const [addComment] = useCreateCommentMutation();
  const [like] = useLikePostMutation();
  const [unlike] = useUnlikePostMutation();
  const [adminLikePost] = useAdminLikeEmployeePostMutation();
  const [adminUnlikePost] = useAdminUnlikeEmployeePostMutation();
  const [adminAddComment] = useAdminCommentOnEmployeePostMutation();
  const [adminLikeComment] = useAdminLikeEmployeeCommentMutation();
  const [adminUnlikeComment] = useAdminUnlikeEmployeeCommentMutation();

  const postPayload = isAdmin ? orgPostResp : employeePostResp;
  const isLoading = isAdmin ? isOrgLoading : isEmpLoading;

  const handleToggleLike = async ({ postId, isLikedByMe }) => {
    const postInner = postPayload?.data;
    const authorUserId = postInner?.user?.id;
    try {
      if (isAdmin) {
        if (!authorUserId) return;
        if (isLikedByMe) {
          await adminUnlikePost({
            userId: authorUserId,
            postId,
          }).unwrap();
        } else {
          await adminLikePost({
            userId: authorUserId,
            postId,
          }).unwrap();
        }
        await refetchOrgPost();
      } else {
        let result;
        if (isLikedByMe) {
          result = await unlike(postId);
        } else {
          result = await like(postId);
        }
        if (!result?.error) await refetchEmployeePost();
      }
    } catch (e) {
      toast.error(e?.data?.message || t("somethingWentWrong"));
    }
  };

  const handleSubmittingComment = async ({
    postId,
    comment,
    setComment,
  }) => {
    const postInner = postPayload?.data;
    const authorUserId = postInner?.user?.id;
    try {
      if (isAdmin) {
        if (!comment?.trim() || !authorUserId) return;
        await adminAddComment({
          userId: authorUserId,
          postId,
          body: { body: comment },
        }).unwrap();
        setComment("");
        await refetchOrgPost();
      } else {
        const dataForm = new FormData();
        dataForm.append("body", comment);
        if (comment) {
          const result = await addComment({ postId, body: dataForm });
          if (!result?.error) {
            await refetchEmployeePost();
            setComment("");
          }
        }
      }
    } catch (e) {
      toast.error(e?.data?.message || t("somethingWentWrong"));
    }
  };

  const handleToggleCommentLike = async (comment, postRow) => {
    const userId = postRow?.user?.id ?? postPayload?.data?.user?.id;
    try {
      if (!userId) return;
      if (comment.is_liked_by_me) {
        await adminUnlikeComment({
          commentId: comment.id,
          userId,
        }).unwrap();
      } else {
        await adminLikeComment({
          commentId: comment.id,
          userId,
        }).unwrap();
      }
      await refetchOrgPost();
    } catch (e) {
      toast.error(e?.data?.message || t("somethingWentWrong"));
    }
  };

  if (isLoading) return null;

  if (!postPayload?.data) {
    return <NotFoundPage />;
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row justify-content-center p-4 community-page pt-0">
        <div className="col-lg-6 col-md-12">
          <ul>
            <li className="mb-3">
              <PostCommunityCard
                post={postPayload?.data}
                handleToggleLike={handleToggleLike}
                handleSubmittingComment={handleSubmittingComment}
                profileBasePath={
                  isAdmin ? "/admin/employee" : "/employee/profile"
                }
                onToggleCommentLike={
                  isAdmin ? handleToggleCommentLike : undefined
                }
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SinglePost;
